import { Injectable, inject } from '@angular/core';
import { Router, ActivatedRoute, UrlTree } from '@angular/router';
import { MenuItem } from 'primeng/api';

import { HisBaseGlobalsService } from '../../service/base/his-base-globals.service';
import { NavigationItem } from '../../model/navigation-item';

@Injectable({providedIn: 'root'})
export class HisNavigationMenuService {
    private hisBaseGlobalsService = inject(HisBaseGlobalsService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);

    private currentAngularAppPrefix = '';
    private flatNavigation: { [k: string]: { item: MenuItem; parent: string } };

    constructor() {

        this.initCurrentAngularAppPrefix();
        this.createFlatNavigation();
    }

    private initCurrentAngularAppPrefix() {
        let temp = window.location.pathname;
        let idx = temp.indexOf( '/a/' );
        if ( idx > -1 ) {
            let idx2 = temp.indexOf( '/', idx + 3 );
            if ( idx2 > -1 ) {
                this.currentAngularAppPrefix = temp.substring( idx, idx2 + 1 );
            } else {
                this.currentAngularAppPrefix = temp.substring( idx ) + '/';
            }
        }
    }

    // all Menu Items with their parent hashed by id
    private createFlatNavigation() {
        this.flatNavigation = {};
        let navigation: MenuItem[] = this.getNavigation();

        for ( let item of navigation ) {
            this.flatItem( item, this.flatNavigation, '' );
        }
    }

    private flatItem( item: MenuItem, flatNavigation: { [k: string]: { item: MenuItem; parent: string } }, parent: string ) {
        if ( item.id ) {
            flatNavigation[item.id] = { item, parent };
        }
        if ( item.items && item.items.length > 0 ) {
            for ( let child of item.items ) {
                this.flatItem( child as MenuItem, flatNavigation, item.id );
            }
        }
    }

    private getNavigation(): MenuItem[] {
        let navigation: NavigationItem = this.hisBaseGlobalsService.getNavigation();
        // console.log('navigation =', navigation);
        let menuItems: MenuItem[] = this.translateItems( navigation.children );
        let bookmarks = this.getBookmarks();
        if ( bookmarks.length > 0 ) {
            menuItems.push( ...bookmarks );
        }
        return menuItems;
    }

    private getBookmarks(): MenuItem[] {
        let bookmarks: NavigationItem = this.hisBaseGlobalsService.getBookmarks();
        if ( bookmarks ) {
            let items: MenuItem[] = this.translateItems( [bookmarks] );
            return items;
        } else {
            return [];
        }
    }

    /*
     * Initial translation from NavigationItem to menu item
     */
    private translateItems( items: NavigationItem[] ): MenuItem[] {
        return items.map( ( item ) => this.translateItem( item ) );
    }

    private translateItem( item: NavigationItem ): MenuItem {
        let menuItem: MenuItem = {};

        // default values
        menuItem.routerLinkActiveOptions = { exact: false };
        menuItem.styleClass = '';

        if ( item.text ) { menuItem.label = item.text; }
        if ( item.iconName ) { menuItem.icon = item.iconName; }
        if ( item.cssclass ) { menuItem.styleClass = item.cssclass; }
        if ( item.target ) { menuItem.target = item.target; }
        if ( item.title || item.description ) { menuItem.title = item.title || item.description; }
        if ( item.name ) { menuItem.id = item.name; }

        // if child pages exist
        if ( item.children && item.children.length > 0 ) {
            menuItem.items = this.translateItems( item.children );
        }

        if ( item.linkType === 'SUBMENU' || item.linkType === 'IMAGE' || ( item.children && item.children.length > 0 ) ) {
            // item is a submenu -> link to Submenu Component
            menuItem.routerLink = ['/hisng/submenu'];
            // set navigation Position to distinguish every submenu
            menuItem.queryParams = { navigationPosition: item.name };
            menuItem.routerLinkActiveOptions = { exact: true };
            // console.log("submenu or image: menuItem =", menuItem);
        } else if ( !item.link ) {
            // ignore menu items without link
        } else if ( item.link.startsWith( 'state=' ) ) {
            // QIS/LSF page
            menuItem.url = '../../rds?' + item.link;
            // console.log("RDS page: menuItem =", menuItem);
        } else if ( item.link.startsWith( this.currentAngularAppPrefix ) ) {
            // item is a link to an angular page of the same servlet mapping -> create routerlink.
            // Note that a router link would not work if we are in one servlet mapping like /a/cs.sys.hisng/
            // and want to call a page from another servlet mapping like /a/fs.res.frontend/
            menuItem.routerLink = this.createRouterLink( item );
            menuItem.queryParams = this.router.parseUrl( item.link ).queryParams;
            // console.log("routerlink to angularpage: menuItem =", menuItem);
        } else {
            // for JSF pages, Angular pages from other servlet mappings or external links we create a simple link
            menuItem.url = '../..' + item.link;
            // console.log("other page type: menuItem =", menuItem);
        }
        // console.log("item linktype", item.linkType);
        // console.log("item link", item.link);
        // console.log("menuItem URL", menuItem.url);

        return menuItem;
    }

    private createRouterLink( item: NavigationItem ): string | string[] {
        let strippedUrl = item.link.substring( this.currentAngularAppPrefix.length );
        let urlPrefix = strippedUrl;
        let pos = strippedUrl.indexOf( '?' );
        if ( pos > -1 ) {
            urlPrefix = strippedUrl.substring( 0, pos );
        }
        return ['/' + urlPrefix];
    }

    private createUrlTree( routerLink: any[] | string, queryParams: { [k: string]: any } ): UrlTree {
        let commands: any[] = [];
        if ( routerLink != null ) {
            commands = Array.isArray( routerLink ) ? routerLink : [routerLink];
        } else {
            commands = [];
        }
        return this.router.createUrlTree( commands, {
            relativeTo: this.route,
            queryParams,
        } );
    }

    public getFlatNavigation() {
        return this.flatNavigation;
    }

    public getMenuItem( name: string ): MenuItem {
        let allItems = this.getFlatNavigation();
        console.log( 'getMenuItem: name =', name, ', allItems =', allItems );
        return allItems[name] ? allItems[name].item : null;
    }

    public getActiveMenuItem(): MenuItem | null {
        let allItems = this.getFlatNavigation();
        // console.log( 'allItems =', allItems );
        let urlMatchItems: MenuItem[] = [];
        for ( let key of Object.keys( allItems ) ) {
            let item: MenuItem = allItems[key].item;
            if ( this.isUrlMatch( item ) ) {
                urlMatchItems.push( item );
            }
        }
        if ( urlMatchItems.length === 0 ) {
            console.log( 'Active menu item test: Nothing found' );
            return null;
        }
        if ( urlMatchItems.length === 1 ) {
            console.log( 'Active menu item test: Found active menu item by unique URL', urlMatchItems[0] );
            return urlMatchItems[0];
        }
        // The URL is ambiguous. Compare navigationPosition with menuItem.id, too
        console.log( 'Active menu item test: Found several URL matches', urlMatchItems );
        let navigationPositionMatchItems: MenuItem[] = [];
        for ( let item of urlMatchItems ) {
            if ( this.isNavigationPositionMatch( item ) ) {
                navigationPositionMatchItems.push ( item );
            }
        }
        if ( navigationPositionMatchItems.length === 0 ) {
            console.log( 'Active menu item test: No navigationPosition matches, return first URL match', urlMatchItems[0] );
            return urlMatchItems[0];
        }
        if ( navigationPositionMatchItems.length === 1 ) {
            console.log( 'Active menu item test: Found active menu item by unique navigationPosition', navigationPositionMatchItems[0] );
            return navigationPositionMatchItems[0];
        }
        // Still ambiguous! (can this happen at all?)
        console.log( 'Active menu item test: Disambiguation by navigationPosition failed, return first navigationPosition match',
            navigationPositionMatchItems[0] );
        return navigationPositionMatchItems[0];
    }

    private isUrlMatch( item: MenuItem ): boolean {
        let currentUrlTree = this.router.parseUrl( this.router.url );
        // console.log( 'check isActive: currentUrlTree=', currentUrlTree );
        let menuItemUrlTree: UrlTree;

        // ignore menu items without link (e. g. bookmark popup) because an empty relative
        // url would match the current address
        if ( !item.routerLink && !item.url ) {
            console.log( 'Active menu item test: ignore item', item );
            return false;
        }

        if ( item.routerLinkActiveOptions.exact ) {
            // Take queryParams into account, except for the parameters noDBAction and init
            // which may be added by redirects e.g. triggered by switching language.
            // See https://hiszilla.his.de/hiszilla/show_bug.cgi?id=199667
            let allParams: { [key: string]: string } = currentUrlTree.queryParams;
            let reducedParams: { [key: string]: string } = {};
            for ( let key of Object.keys( allParams ) ) {
                if ( key !== 'noDBAction' && key !== 'init' ) {
                    reducedParams[key] = allParams[key];
                }
            }
            currentUrlTree.queryParams = reducedParams;
            menuItemUrlTree = this.createUrlTree( item.routerLink || item.url, item.queryParams );
        } else {
            // Ignore queryParams.
            // https://github.com/angular/angular/issues/13205#issuecomment-327703678
            currentUrlTree.queryParams = {};
            menuItemUrlTree = this.createUrlTree( item.routerLink || item.url, {} );
        }
        // console.log( 'Active menu item test: menuItemUrlTree=', menuItemUrlTree );
        if ( currentUrlTree.toString() === menuItemUrlTree.toString() ) {
            console.log( 'Active menu item test: Found URL match:', item );
            return true;
        }
        return false;
    }

    private isNavigationPositionMatch( item: MenuItem ): boolean {
        // In case that several menu points have the same URL, we can also compare the navigationPosition if given
        let navigationPositionUrlParam = this.route.snapshot.queryParams.navigationPosition;
        if ( navigationPositionUrlParam ) {
            console.log( 'Active menu item test: Explicit navigationPosition =', navigationPositionUrlParam );
            let navigationItemNames: string[] = navigationPositionUrlParam.split( ',' ).map( (elem: string) => elem.trim() );
            console.log( 'Active menu item test: navigationItemNames =', navigationItemNames, ', item.id =', item.id );
            if ( navigationItemNames.length > 0 ) {
                let lastItemName = navigationItemNames[navigationItemNames.length - 1];
                console.log( 'Active menu item test: lastItemName =', lastItemName );
                if ( lastItemName === item.id ) {
                    console.log( 'Active menu item test: Found navigationPositionMatch', item );
                    return true;
                } else {
                    console.log( 'Active menu item test: navigationPosition does not match for', item );
                    return false;
                }
            }
        }
        console.log( 'Active menu item test: Could not compare navigationPosition for', item );
        return false;
    }

    public getParentsOfMenuItem( item: MenuItem ): MenuItem[] {
        let navigation = this.getFlatNavigation();
        let menuItemKey = item ? item.id : '';
        let items: MenuItem[] = new Array<MenuItem>();

        while ( menuItemKey !== '' && navigation[menuItemKey] ) {
            items.push( navigation[menuItemKey].item );
            menuItemKey = navigation[menuItemKey].parent;
        }

        items = items.reverse();
        return items;
    }
}
